import React, { useEffect, useState } from 'react';
import * as motion from 'motion/react-m';

/**
 * Animated SVG uses in the NotFoundPage, receives computed x, y coordinates from parent.
 */
const AnimatedMagnifyingGlassIcon = () => {
  const [{ x, y }, setPosition] = useState({ x: 0, y: 0 });

  const move = (e: MouseEvent) => {
    let moveX = (e.pageX - window.innerWidth / 2) / 10;
    let moveY = (e.pageY - window.innerHeight / 2) / 10;

    // Clamp to 50 units (500px)
    if (moveX > 50) moveX = 50;
    if (moveX < -50) moveX = -50;

    if (moveY > 50) moveY = 50;
    if (moveY < -50) moveY = -50;

    setPosition({ x: moveX, y: moveY });
  };

  useEffect(() => {
    window.addEventListener('mousemove', move);

    return () => window.removeEventListener('mousemove', move);
  }, []);

  return (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 7600 5335"
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
      xmlSpace="preserve"
      fillRule="evenodd"
      clipRule="evenodd"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeMiterlimit="1.5"
    >
      <rect
        id="Artboard1"
        x="0"
        y="0"
        width="7599.18"
        height="5334.74"
        fill="none"
      />
      <motion.g
        id="mo"
        // Multiplier is extent to which the part moves with the mouse
        // parts farther away should move less, closer should move more (e.g. magnifying glass)
        animate={{
          x: `${x * 2.5}px`,
          y: `${y * 2.5}px`,
        }}
      >
        <g id="body">
          <path
            d="M5345.29,2836.62c0.82,3.072 92.666,266.278 33.051,642.692c-116.887,738.028 -756.653,1303.02 -1527.34,1303.02c-750.767,0 -1377.29,-536.165 -1517.23,-1246.15c-5.67,-28.767 -40.575,201.368 -143.829,319.522c-98.835,113.099 -157.207,40.006 -206.692,-12.714c-54.582,-58.151 -121.197,-288.206 12.24,-488.056c140.369,-210.232 318.911,-295.85 322.142,-320.62c98.915,-758.387 748.199,-1344.82 1533.37,-1344.82c715.442,0 1318.06,486.899 1494.29,1147.13Z"
            fill="#d4e3fa"
            stroke="#3976e3"
            strokeWidth="95.83px"
          />
          <g>
            <path
              d="M4618.75,4277.69c121.942,-121.362 237.223,-415.711 214.816,-597.42c-22.408,-181.71 -86.064,-399.233 -415.926,-456.208c-329.861,-56.975 -371.218,-87.827 -632.804,-268.161c-261.586,-180.334 -709.222,-196.749 -871.102,0c-161.88,196.749 -379.983,469.163 -252.692,856.129c127.291,386.965 465.584,634.275 657.554,707.503c299.503,114.246 574.239,93.454 782.717,28.831c208.479,-64.623 395.495,-149.312 517.437,-270.674Z"
              fill="#91b8f2"
            />
            <path
              d="M2986.75,4049.65c0,0 66.304,110.684 202.376,225.138c47.628,40.061 103.929,80.039 168.907,114.1c23.712,12.43 48.855,23.532 75.067,33.351c37.458,14.032 76.892,25.842 118.39,34.397c93.515,19.279 194.602,24.716 303.571,12.232c15.586,-0.263 28.026,-13.13 27.764,-28.715c-0.263,-15.586 -13.13,-28.027 -28.716,-27.764c-100.317,-8.078 -189.172,-33.777 -271.201,-64.554c-35.333,-13.257 -68.743,-27.937 -101.142,-42.756c-22.636,-10.353 -44.543,-20.986 -66.013,-31.276c-59.787,-28.656 -114.144,-58.174 -161.812,-88.023c-139.504,-87.355 -222.382,-170.524 -222.382,-170.524c-9.491,-12.366 -27.236,-14.699 -39.601,-5.208c-12.366,9.492 -14.699,27.237 -5.208,39.602l0,0Z"
              fill="#e9f0fd"
            />
          </g>
        </g>
        <g>
          <motion.g
            id="mouth"
            animate={{
              x: `${x * 5}px`,
              y: `${y * 5}px`,
            }}
          >
            <path
              d="M3520.17,2608.22c0,0 21.245,-42.57 56.37,-77.5c20.24,-20.128 45.308,-37.573 74.911,-40.303c19.264,-1.776 40.268,2.866 63.544,17.177c25.245,15.52 52.848,40.777 82.317,80.165c5.288,7.663 15.803,9.592 23.467,4.304c7.663,-5.288 9.591,-15.803 4.303,-23.467c-29.851,-47.017 -58.893,-78.092 -86.491,-97.171c-32.515,-22.477 -63.609,-29.228 -91.462,-26.505c-40.28,3.939 -74.729,28.332 -100.642,57.291c-37.479,41.888 -57.219,92.465 -57.219,92.465c-3.738,8.528 0.151,18.486 8.679,22.224c8.528,3.737 18.486,-0.152 22.223,-8.68Z"
              fill="#2d5fb7"
            />
            <path
              d="M3516.97,2564.04c0,-0 -39.911,-101.253 24.333,-123.207c64.245,-21.954 85.566,35.096 85.566,35.096c0,0 -76.153,25.296 -109.899,88.111Z"
              fill="#2d5fb7"
              stroke="#2d5fb7"
              strokeWidth="22.92px"
            />
          </motion.g>
          <motion.g
            id="glasses"
            animate={{
              x: `${x * 10}px`,
              y: `${y * 10}px`,
            }}
          >
            <circle cx="4500.43" cy="2467.22" r="549.559" fill="none" />
            <clipPath id="_clip1">
              <circle cx="4500.43" cy="2467.22" r="549.559" />
            </clipPath>
            <g clipPath="url(#_clip1)">
              <ellipse
                cx="4537.41"
                cy="2486.92"
                rx="1003.29"
                ry="870.013"
                fill="#fff"
              />
              <path
                d="M4537.41,1588.66c-272.696,-4.857 -522.236,83.174 -709.799,229.669c-210.62,164.504 -342.245,403.037 -346.411,668.587c-3.046,268.445 125.129,512.615 333.892,684.074c188.429,154.759 442.886,249.138 722.318,250.313c279.247,-1.743 533.233,-96.623 721.376,-251.479c208.222,-171.382 336.345,-414.927 333.286,-682.908c-3.929,-265.17 -135.288,-503.395 -345.431,-667.884c-187.323,-146.625 -436.653,-234.892 -709.231,-230.372Zm0,56.487c255.746,4.24 487.012,93.791 658.363,236.811c183.215,152.923 297.073,367.825 293.56,604.958c-2.675,234.317 -121.023,443.317 -305.705,589.933c-170.192,135.111 -397.195,217.259 -646.218,215.705c-248.831,1.046 -475.357,-81.687 -645.274,-216.872c-184.162,-146.519 -302.445,-354.913 -305.099,-588.766c-3.714,-236.759 109.811,-451.379 292.579,-604.254c171.132,-143.143 402.158,-232.963 657.794,-237.515Z"
                fill="#e9f0fd"
              />
            </g>
            <circle
              cx="4500.43"
              cy="2467.22"
              r="549.559"
              fill="none"
              stroke="#30a919"
              strokeWidth="62.5px"
            />
            <circle cx="2815.48" cy="2467.22" r="549.559" fill="none" />
            <clipPath id="_clip2">
              <circle cx="2815.48" cy="2467.22" r="549.559" />
            </clipPath>
            <g clipPath="url(#_clip2)">
              <ellipse
                cx="2852.46"
                cy="2486.92"
                rx="1003.29"
                ry="870.013"
                fill="#fff"
              />
              <path
                d="M2852.46,1588.66c-272.695,-4.857 -522.236,83.174 -709.799,229.669c-210.62,164.504 -342.245,403.037 -346.411,668.587c-3.046,268.445 125.129,512.615 333.892,684.074c188.429,154.759 442.886,249.138 722.318,250.313c279.247,-1.743 533.233,-96.623 721.376,-251.479c208.223,-171.382 336.345,-414.927 333.286,-682.908c-3.928,-265.17 -135.288,-503.395 -345.431,-667.884c-187.323,-146.625 -436.653,-234.892 -709.231,-230.372Zm0,56.487c255.746,4.24 487.012,93.791 658.363,236.811c183.215,152.923 297.073,367.825 293.56,604.958c-2.675,234.317 -121.023,443.317 -305.705,589.933c-170.192,135.111 -397.195,217.259 -646.218,215.705c-248.831,1.046 -475.357,-81.687 -645.274,-216.872c-184.162,-146.519 -302.445,-354.913 -305.099,-588.766c-3.714,-236.759 109.811,-451.379 292.579,-604.254c171.132,-143.143 402.158,-232.963 657.794,-237.515Z"
                fill="#e9f0fd"
              />
            </g>
            <circle
              cx="2815.48"
              cy="2467.22"
              r="549.559"
              fill="none"
              stroke="#30a919"
              strokeWidth="62.5px"
            />
            <path
              d="M3278.95,2168.36c0,-0 158.741,-93.064 398.279,-93.4c212.113,-0.297 345.866,98.239 345.866,98.239"
              fill="none"
              stroke="#30a919"
              strokeWidth="62.5px"
            />
          </motion.g>
          <motion.g
            id="eyes"
            animate={{
              x: `${x * 5}px`,
              y: `${y * 5}px`,
            }}
          >
            <g id="left-eye-group">
              <path
                id="left-eyebrow"
                d="M3142.91,2368.27c-11.35,-30.058 -38.952,-49.921 -76.889,-60.682c-25.83,-7.327 -56.774,-10.245 -90.072,-9.377c-17.097,0.446 -34.839,1.906 -52.773,4.438c-17.128,2.419 -34.444,5.778 -51.571,9.923c-54.2,13.118 -106.612,33.208 -147.106,54.206c-8.411,3.994 -11.998,14.064 -8.004,22.475c3.993,8.411 14.063,11.998 22.474,8.005c39.501,-17.08 90.373,-31.34 142.047,-41.229c16.207,-3.102 32.478,-5.78 48.521,-8.014c16.503,-2.297 32.742,-4.157 48.441,-5.185c35.186,-2.305 67.457,-1.008 92.73,7.326c18.832,6.209 33.82,15.544 41.098,31.189c3.608,8.583 13.506,12.622 22.089,9.014c8.584,-3.608 12.623,-13.506 9.015,-22.089Z"
                fill="#2d5fb7"
              />
              <ellipse
                id="left-eye"
                cx="2953.53"
                cy="2535.34"
                rx="101.071"
                ry="120.462"
                fill="#2d5fb7"
              />
            </g>
            <g id="right-eye-group">
              <path
                id="right-eyebrow"
                d="M4698.17,2406.56c-4.184,-80.489 -20.648,-142.523 -44.732,-188.166c-25.949,-49.176 -60.529,-80.298 -96.562,-96.104c-77.471,-33.985 -162.968,-0.171 -202.055,73.323c-4.597,8.097 -1.756,18.403 6.341,23c8.097,4.597 18.403,1.756 23,-6.34c20.714,-33.93 54.297,-55.006 90.666,-59.55c33.438,-4.177 68.853,6.946 100.069,35.671c24.227,22.296 46.01,54.644 62.37,99.384c12.319,33.688 22.067,73.82 27.268,121.448c0.736,9.281 8.868,16.22 18.15,15.484c9.282,-0.736 16.22,-8.868 15.485,-18.15Z"
                fill="#2d5fb7"
              />
              <ellipse
                id="right-eye"
                cx="4441.41"
                cy="2535.34"
                rx="101.071"
                ry="120.462"
                fill="#2d5fb7"
              />
            </g>
          </motion.g>
        </g>
      </motion.g>
      <motion.g
        id="magnifying-glass-group"
        animate={{
          x: `${x * 30}px`,
          y: `${y * 30}px`,
        }}
      >
        <g id="magnifying-glass">
          <path
            d="M4370.55,1702.87c431.449,-79.804 846.521,205.687 926.324,637.136c79.804,431.449 -205.687,846.52 -637.136,926.324c-431.448,79.804 -846.52,-205.688 -926.324,-637.136c-79.803,-431.449 205.688,-846.521 637.136,-926.324Z"
            fill="none"
          />
          <clipPath id="_clip3">
            <path d="M4370.55,1702.87c431.449,-79.804 846.521,205.687 926.324,637.136c79.804,431.449 -205.687,846.52 -637.136,926.324c-431.448,79.804 -846.52,-205.688 -926.324,-637.136c-79.803,-431.449 205.688,-846.521 637.136,-926.324Z" />
          </clipPath>
          <g clipPath="url(#_clip3)">
            <motion.g
              id="mo-large"
              animate={{
                x: `${-x * 30}px`,
                y: `${-y * 30}px`,
              }}
            >
              <g id="body-large">
                <path
                  d="M6044.88,3050.57c1.475,5.524 166.617,478.778 59.427,1155.58c-210.166,1327 -1360.49,2342.88 -2746.21,2342.88c-1349.9,0 -2476.42,-964.044 -2728.04,-2240.62c-10.195,-51.725 -72.956,362.066 -258.61,574.512c-177.709,203.355 -282.664,71.931 -371.639,-22.861c-98.141,-104.557 -217.917,-518.205 22.008,-877.543c252.388,-378.005 573.413,-531.948 579.222,-576.485c177.853,-1363.61 1345.29,-2418.04 2757.06,-2418.04c1286.39,0 2369.92,875.461 2686.78,2062.57Z"
                  fill="#d4e3fa"
                  stroke="#3976e3"
                  strokeWidth="172.31px"
                />
                <g>
                  <path
                    d="M4738.53,5641.67c219.256,-218.213 426.537,-747.463 386.247,-1074.18c-40.289,-326.721 -154.746,-717.835 -747.849,-820.278c-593.103,-102.443 -667.463,-157.916 -1137.81,-482.163c-470.341,-324.248 -1275.21,-353.762 -1566.27,-0c-291.066,353.761 -683.224,843.572 -454.349,1539.35c228.874,695.778 837.136,1140.45 1182.31,1272.12c538.518,205.419 1032.5,168.035 1407.36,51.84c374.852,-116.195 711.113,-268.469 930.369,-486.682Z"
                    fill="#91b8f2"
                  />
                  <path
                    d="M1804.15,5231.63c0,-0 119.216,199.014 363.88,404.806c85.636,72.031 186.867,143.913 303.7,205.156c42.636,22.35 87.844,42.312 134.973,59.967c67.351,25.229 138.255,46.465 212.87,61.847c168.144,34.664 349.902,44.44 545.832,21.994c28.024,-0.473 50.392,-23.608 49.92,-51.632c-0.472,-28.024 -23.608,-50.392 -51.632,-49.92c-180.374,-14.525 -340.139,-60.732 -487.629,-116.071c-63.531,-23.836 -123.603,-50.231 -181.857,-76.876c-40.7,-18.616 -80.09,-37.734 -118.694,-56.237c-107.499,-51.523 -205.235,-104.599 -290.944,-158.268c-250.834,-157.067 -399.85,-306.608 -399.85,-306.608c-17.066,-22.233 -48.972,-26.429 -71.206,-9.363c-22.233,17.066 -26.428,48.972 -9.363,71.205l0,-0Z"
                    fill="#e9f0fd"
                  />
                </g>
              </g>
              <g>
                <motion.g
                  id="mouth-large"
                  animate={{
                    x: `${x * 10}px`,
                    y: `${y * 10}px`,
                  }}
                >
                  <path
                    d="M2763.24,2639.9c0,-0 38.199,-76.542 101.355,-139.348c36.393,-36.191 81.467,-67.557 134.693,-72.466c34.638,-3.194 72.403,5.154 114.255,30.884c45.391,27.905 95.023,73.32 148.009,144.14c9.508,13.779 28.414,17.247 42.193,7.738c13.779,-9.508 17.247,-28.414 7.739,-42.193c-53.674,-84.538 -105.893,-140.413 -155.515,-174.717c-58.463,-40.415 -114.37,-52.554 -164.452,-47.657c-72.424,7.082 -134.366,50.941 -180.958,103.012c-67.389,75.315 -102.883,166.255 -102.883,166.255c-6.72,15.333 0.273,33.238 15.606,39.958c15.334,6.72 33.238,-0.273 39.958,-15.606l0,-0Z"
                    fill="#2d5fb7"
                  />
                  <path
                    d="M2757.49,2560.46c-0,0 -71.763,-182.056 43.751,-221.53c115.515,-39.473 153.851,63.105 153.851,63.105c-0,-0 -136.926,45.483 -197.602,158.425Z"
                    fill="#2d5fb7"
                    stroke="#2d5fb7"
                    strokeWidth="41.21px"
                  />
                </motion.g>
                <motion.g
                  id="glasses-large"
                  animate={{
                    x: `${x * 10}px`,
                    y: `${y * 10}px`,
                  }}
                >
                  <circle cx="4525.8" cy="2386.37" r="988.126" fill="none" />
                  <clipPath id="_clip4">
                    <circle cx="4525.8" cy="2386.37" r="988.126" />
                  </clipPath>
                  <g clipPath="url(#_clip4)">
                    <ellipse
                      cx="4592.29"
                      cy="2421.79"
                      rx="1803.95"
                      ry="1564.31"
                      fill="#fff"
                    />
                    <path
                      d="M4592.29,829.231c-483.465,-4.789 -924.938,153.925 -1255.49,416.084c-367.382,291.365 -597.314,710.521 -601.38,1176.47c-2.959,468.849 223.234,893.774 588.861,1191.96c331.499,270.354 777.797,435.581 1268.01,436.727c490.03,-1.701 935.855,-167.448 1267.07,-437.894c365.091,-298.105 591.227,-722.408 588.254,-1190.79c-3.836,-465.574 -233.488,-884.428 -600.4,-1175.77c-330.32,-262.288 -771.576,-421.246 -1254.92,-416.788Zm0,56.487c466.515,4.302 889.71,164.546 1204.06,423.226c339.982,279.775 552.133,675.31 548.528,1112.85c-2.76,434.72 -219.124,824.481 -560.674,1097.82c-313.264,250.701 -732.107,403.717 -1191.91,402.121c-459.615,1.074 -877.982,-152.507 -1190.97,-403.287c-341.026,-273.246 -557.327,-662.397 -560.067,-1096.65c-3.815,-437.16 208.019,-832.406 547.547,-1112.14c314.124,-258.805 737.085,-419.31 1203.49,-423.93Z"
                      fill="#e9f0fd"
                    />
                  </g>
                  <circle
                    cx="4525.8"
                    cy="2386.37"
                    r="988.126"
                    fill="none"
                    stroke="#30a919"
                    strokeWidth="112.38px"
                  />
                  <circle cx="1496.19" cy="2386.37" r="988.126" fill="none" />
                  <clipPath id="_clip5">
                    <circle cx="1496.19" cy="2386.37" r="988.126" />
                  </clipPath>
                  <g clipPath="url(#_clip5)">
                    <ellipse
                      cx="1562.68"
                      cy="2421.79"
                      rx="1803.95"
                      ry="1564.31"
                      fill="#fff"
                    />
                    <path
                      d="M1562.68,829.231c-483.465,-4.789 -924.938,153.925 -1255.49,416.084c-367.382,291.365 -597.315,710.521 -601.381,1176.47c-2.959,468.849 223.234,893.774 588.861,1191.96c331.499,270.354 777.797,435.581 1268.01,436.727c490.03,-1.701 935.855,-167.448 1267.07,-437.894c365.091,-298.105 591.228,-722.408 588.254,-1190.79c-3.836,-465.574 -233.487,-884.428 -600.4,-1175.77c-330.32,-262.288 -771.576,-421.246 -1254.92,-416.788Zm0,56.487c466.515,4.302 889.71,164.546 1204.06,423.226c339.982,279.775 552.133,675.31 548.528,1112.85c-2.759,434.72 -219.123,824.481 -560.674,1097.82c-313.263,250.701 -732.107,403.717 -1191.91,402.121c-459.614,1.074 -877.982,-152.507 -1190.97,-403.287c-341.026,-273.246 -557.327,-662.397 -560.067,-1096.65c-3.815,-437.16 208.019,-832.406 547.547,-1112.14c314.125,-258.805 737.085,-419.31 1203.49,-423.93Z"
                      fill="#e9f0fd"
                    />
                  </g>
                  <circle
                    cx="1496.19"
                    cy="2386.37"
                    r="988.126"
                    fill="none"
                    stroke="#30a919"
                    strokeWidth="112.38px"
                  />
                  <path
                    d="M2329.53,1849.01c-0,-0 285.422,-167.333 716.118,-167.937c381.388,-0.534 621.881,176.638 621.881,176.638"
                    fill="none"
                    stroke="#30a919"
                    strokeWidth="112.38px"
                  />
                </motion.g>
                <motion.g
                  id="eyes-large"
                  animate={{
                    x: `${x * 10}px`,
                    y: `${y * 10}px`,
                  }}
                >
                  <g>
                    <path
                      d="M2084.93,2208.46c-20.406,-54.045 -70.037,-89.76 -138.249,-109.108c-46.443,-13.174 -102.082,-18.421 -161.953,-16.86c-30.741,0.801 -62.641,3.426 -94.888,7.98c-30.796,4.349 -61.931,10.388 -92.726,17.841c-97.454,23.586 -191.693,59.71 -264.502,97.465c-15.123,7.18 -21.572,25.288 -14.392,40.411c7.18,15.123 25.287,21.573 40.411,14.393c71.022,-30.71 162.494,-56.351 255.405,-74.132c29.14,-5.577 58.395,-10.392 87.241,-14.408c29.673,-4.131 58.871,-7.475 87.099,-9.324c63.266,-4.145 121.29,-1.813 166.733,13.172c33.859,11.165 60.808,27.95 73.895,56.08c6.488,15.433 24.285,22.695 39.718,16.208c15.433,-6.488 22.696,-24.285 16.208,-39.718Z"
                      fill="#2d5fb7"
                    />
                    <ellipse
                      cx="1744.41"
                      cy="2508.86"
                      rx="181.729"
                      ry="216.596"
                      fill="#2d5fb7"
                    />
                  </g>
                  <g>
                    <path
                      d="M4881.34,2277.3c-7.522,-144.722 -37.125,-256.261 -80.43,-338.329c-46.657,-88.421 -108.833,-144.379 -173.62,-172.8c-139.296,-61.105 -293.023,-0.306 -363.303,131.838c-8.266,14.559 -3.157,33.089 11.401,41.355c14.558,8.266 33.088,3.158 41.355,-11.4c37.245,-61.006 97.628,-98.902 163.021,-107.072c60.123,-7.511 123.8,12.489 179.926,64.138c43.563,40.088 82.729,98.252 112.145,178.695c22.15,60.572 39.678,132.731 49.029,218.368c1.322,16.689 15.945,29.164 32.634,27.841c16.689,-1.322 29.165,-15.945 27.842,-32.634Z"
                      fill="#2d5fb7"
                    />
                    <ellipse
                      cx="4419.68"
                      cy="2508.86"
                      rx="181.729"
                      ry="216.596"
                      fill="#2d5fb7"
                    />
                  </g>
                </motion.g>
              </g>
            </motion.g>
            <path
              d="M4370.55,1702.87c431.449,-79.804 846.521,205.687 926.324,637.136c79.804,431.449 -205.687,846.52 -637.136,926.324c-431.448,79.804 -846.52,-205.688 -926.324,-637.136c-79.803,-431.449 205.688,-846.521 637.136,-926.324Z"
              fill="#fff"
              fillOpacity="0.41"
            />
            <path
              d="M4197.47,2945.55c128.061,102.408 597.571,191.449 811.778,-192.981"
              fill="none"
              stroke="#fff"
              strokeOpacity="0.3"
              strokeWidth="95.83px"
            />
          </g>
          <path
            d="M4370.55,1702.87c431.449,-79.804 846.521,205.687 926.324,637.136c79.804,431.449 -205.687,846.52 -637.136,926.324c-431.448,79.804 -846.52,-205.688 -926.324,-637.136c-79.803,-431.449 205.688,-846.521 637.136,-926.324Z"
            fill="none"
            stroke="#2e5583"
            strokeWidth="158.33px"
          />
          <path
            d="M5186.93,2909.42l793.506,527.594"
            fill="none"
            stroke="#2e5583"
            strokeWidth="158.33px"
          />
        </g>
        <path
          id="hand"
          fill="#d4e3fa"
          stroke="#3976e3"
          d="M5307.29,3160.42c-0,0 83.24,-202.814 231.326,-248.781c148.087,-45.967 274.857,148.972 323.36,241.562c48.504,92.591 52.591,359.389 -150.422,396.94c-203.014,37.551 -422.014,-218.02 -404.264,-389.721Z"
          strokeWidth="95.83px"
        />
      </motion.g>
    </svg>
  );
};

export default AnimatedMagnifyingGlassIcon;
